import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import PublicPage from "@/components/PublicPage";
import { COMPANY } from "@/lib/company";
import { cn } from "@/lib/utils";

const GROUPS = [
  {
    group: "About the Platform",
    items: [
      [
        "What is Plan-Sarthi?",
        "Plan-Sarthi is an industrial EPC execution platform. It joins high-level planning (yearly, monthly and weekly targets) to what actually happens on site through the Daily Progress Report, and then recomputes everything downstream automatically — balance quantities, run-rates, S-curves, the critical path, earned value and cost forecasts. One workspace replaces the usual stack of disconnected spreadsheets.",
      ],
      [
        "Who is it built for?",
        "Contractors, EPC companies and project-management consultancies executing heavy industrial work: thermal power plants, T&D and substations, solar parks, refinery and petrochemical units, nuclear facilities and large infrastructure. Inside a project it is used by planning engineers, site engineers, HODs, project managers, storekeepers and management — each with their own role-based view.",
      ],
      [
        "How is it different from generic project management tools?",
        "Generic tools track tasks. Plan-Sarthi tracks quantities, money and float. It understands Cu.M of concrete, MT of structural steel and Dia-Inch of piping; it computes CPI and SPI from validated physical progress rather than from someone's percentage guess; it prices every hindrance day against the critical path; and it produces the reporting pack a client review actually asks for. It also assumes site reality — night shifts, idle manpower, weather, no network signal.",
      ],
      [
        "Is my project data secure?",
        "Yes. All traffic is TLS-encrypted, passwords are stored only as bcrypt hashes, sessions use short-lived signed tokens in httpOnly cookies, and role-based permissions control who can see or change what. Your data is never shared with another customer, never sold, and never used to train third-party AI models. Full detail is in our Privacy Policy.",
      ],
    ],
  },
  {
    group: "Getting Started",
    items: [
      [
        "How do I sign up?",
        "Go to the signup page, enter your name, company, designation, mobile number and work email, and choose a password. We email a verification code; once you confirm it your workspace opens and the 14-day trial clock starts. Registration is intended for companies, so please use a genuine work email address.",
      ],
      [
        "What is included in the 14-day free trial?",
        "Everything in the planning and execution suite: projects, WBS and quantities, DPR, tiered L1/L2/L3 planning, critical path, hindrance matrix, resources and manpower, EVM costing, the what-if modeller, store and procurement, employee KPIs and the full export centre. The AI modules — Risk Radar, AI risk briefs and the AI BOQ Parser — are paid features and are not included in the trial.",
      ],
      [
        "Do I need to enter payment details to start the trial?",
        "No. The trial requires no card, no UPI mandate and no payment details of any kind. Nothing is charged automatically, and the trial simply stops at day 14 unless you choose to upgrade.",
      ],
      [
        "How do I upgrade to a paid plan?",
        "Open Plans & billing from the sidebar or the portfolio header, pick Starter or Professional (or a per-project licence), and pay through Razorpay. Access, including the AI modules on Professional, is unlocked the moment the payment is verified — usually within seconds — and a receipt is emailed to you.",
      ],
    ],
  },
  {
    group: "Features",
    items: [
      [
        "What is the Risk Radar?",
        "Risk Radar is the predictive layer. It compares each activity's lifetime run-rate against its trailing 21-day rate, classifies the trend as accelerating, steady or decelerating, measures slippage against plan, and forecasts a finish date and days late per activity. Fronts are banded red, amber or green, rolled into a project health score with a confidence level based on how much DPR history exists. It can also write a management brief — verdict, causes and recovery actions. Risk Radar requires a paid plan.",
      ],
      [
        "How does the BOQ Parser work?",
        "Upload a BOQ as Excel, CSV or PDF (up to 30 MB). Two engines run: a deterministic column and pattern parser, and an AI extraction pass. Where both agree, confidence is raised and the row is marked as a double match. Units are normalised (Cum to Cu.M, Mtr to RMT, Ton to MT), disciplines are inferred and every row gets a confidence score. You then review the grid, edit any cell — which locks that row as planner-verified — exclude what you do not want, and confirm. Accepted rows become Level-3 activities under a new WBS group, optionally chained finish-to-start so the critical path is ready immediately.",
      ],
      [
        "Can I import Excel files for WBS and BOQ?",
        "Yes, through the BOQ Parser, which accepts .xlsx, .xlsm, .csv and .pdf and writes the confirmed rows straight into your WBS as activities with quantities, units and rates. Export works in the other direction too: Excel data packs, PPTX decks, A4/A3 PDFs and a live Power BI feed.",
      ],
      [
        "Does Plan-Sarthi work on mobile?",
        "Yes. The interface is responsive down to phone width, which matters most for the DPR — a site engineer can log the day's quantities, manpower split, idle hours and weather from a handset. It also works with no signal at all: entries are stored on the device and sync automatically the moment connectivity returns.",
      ],
    ],
  },
  {
    group: "Billing and Payments",
    items: [
      [
        "What payment methods are accepted?",
        "All methods supported by Razorpay: UPI, credit and debit cards, netbanking and wallets. Everything is billed in Indian Rupees. We never see or store your card number, CVV or UPI PIN — those are captured by Razorpay on PCI-DSS compliant infrastructure.",
      ],
      [
        "Can I get a refund?",
        "Subscription fees are generally non-refundable because access is granted immediately. We do refund duplicate charges, and cases where a fault on our side blocked your paid access for more than five consecutive working days and we could not fix it. Write to " + COMPANY.email + " within 7 days of the charge; approved refunds go back through Razorpay in about 7–10 working days. Unused AI credits are not refundable.",
      ],
      [
        "What happens when my trial expires?",
        "The workspace locks and every data module returns a locked state, but nothing is deleted. Every project, DPR, quantity and report you created is retained exactly as it was, and the moment you upgrade it all becomes available again. The pricing and billing pages stay reachable while locked so you can upgrade without help from us.",
      ],
      [
        "How do I cancel my subscription?",
        "There is no silent auto-renewal to escape: your plan simply runs to the end of its paid period. To close the account entirely, email " + COMPANY.email + " from your registered address and we will confirm in writing. You have 30 days after closure to export your data.",
      ],
    ],
  },
  {
    group: "Data and Security",
    items: [
      [
        "Where is my data stored?",
        "The primary application database is hosted on cloud infrastructure with Indian data residency. A small number of sub-processors — the payment gateway, the email provider and the AI gateway — may process limited data outside India under contractual safeguards. The list is published in our Privacy Policy.",
      ],
      [
        "Can I export my project data?",
        "Yes, at any time and without asking us. The Export Center produces a seven-sheet Excel data pack, a board-ready PPTX deck, A4 and A3 print PDFs, and an OData feed for Power BI. If you want a complete machine-readable dump of a workspace, email us and we will provide it.",
      ],
      [
        "What happens to my data if I cancel?",
        "It is retained for 30 days after cancellation so you can reactivate or export it, then deleted from the live systems, with backups purged within 35 days. Invoices and billing records are kept for eight years because Indian tax law requires it. You can ask for earlier deletion in writing.",
      ],
    ],
  },
  {
    group: "Support",
    items: [
      [
        "How do I contact support?",
        "Email " + COMPANY.email + " from your registered address. We answer on working days between 9:30 AM and 6:30 PM IST. Quote your company name and, if it concerns a specific package, the project name — it lets us reproduce the issue much faster.",
      ],
      [
        "Is there onboarding assistance for my team?",
        "Yes. For paid plans we help set up your first project package: WBS structure and coding, BOQ import, baseline dates and logic, role-based user accounts and the reporting pack your client expects. We also run a walkthrough for planning and site engineers so the DPR discipline sticks from day one. Write to us to schedule it.",
      ],
    ],
  },
];

const Item = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border bg-card" data-testid={`faq-item-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-3 text-left px-4 py-3.5 hover:bg-muted transition-colors duration-150"
        data-testid={`faq-question-${id}`}
      >
        <span className="text-[13.5px] sm:text-sm font-medium flex-1">{q}</span>
        <ChevronDown className={cn("w-4 h-4 mt-0.5 shrink-0 text-[#F97316] transition-transform duration-200", open && "rotate-180")} />
      </button>
      {open ? (
        <p className="px-4 pb-4 text-[13px] text-muted-foreground leading-relaxed" data-testid={`faq-answer-${id}`}>
          {a}
        </p>
      ) : null}
    </div>
  );
};

export default function FAQ() {
  let n = 0;
  return (
    <PublicPage
      testId="faq-page"
      title="Frequently Asked Questions"
      updated={false}
      meta={{
        title: "Plan-Sarthi FAQ — Trial, Features, Billing, Data & Security",
        description:
          "Answers on the Plan-Sarthi platform, the 14-day free trial, Risk Radar and BOQ Parser, billing in INR via Razorpay, data storage in India, exports and support.",
        path: "/faq",
      }}
      intro="Everything companies usually ask before putting Plan-Sarthi on a live project — the platform, the trial, the features, billing, data and support. If your question isn't here, write to us and we will answer it properly."
    >
      {GROUPS.map((g) => (
        <section key={g.group} data-testid={`faq-group-${g.group.replace(/\s|\//g, "-").toLowerCase()}`}>
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-[#F97316]">{g.group}</h2>
          <div className="mt-3.5 space-y-2.5">
            {g.items.map(([q, a]) => {
              n += 1;
              return <Item key={q} q={q} a={a} id={n} />;
            })}
          </div>
        </section>
      ))}

      <section className="border border-[#F97316]/40 bg-[#F97316]/5 p-5" data-testid="faq-contact-cta">
        <h2 className="text-base md:text-lg font-semibold">Still have a question?</h2>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
          Write to{" "}
          <a href={`mailto:${COMPANY.email}`} className="text-[#F97316] hover:underline" data-testid="faq-contact-email">
            {COMPANY.email}
          </a>{" "}
          and a person who knows the platform will reply — or{" "}
          <Link to="/signup" className="text-[#F97316] hover:underline" data-testid="faq-signup-link">
            start the 14-day free trial
          </Link>{" "}
          and see it on your own project.
        </p>
      </section>
    </PublicPage>
  );
}
